import create from 'zustand'
import performance from '../performance'
import logger from '../logger'
import { readRemoteFile } from 'react-papaparse'

const DATA_VERSION = 13

const SHOW_NATIVE_AMERICAN = 0
const SHOW_PUERTO_RICO = 0

const prefixes = ['all','a','b','p','f','h','m','mf','i','np','pn','wa','wb','wh','w','wi']
const subjects = ['ela','mth','abs']
const years = [2009,2010,2011,2012,2013,2014,2015,2016,2017,2018,2019,2020,2021,2022,2023,2024,1922,1923,1924,2224]
const units = [
  null,
  // 'ns'
]
const suffixes = [null,'e', 'simavg', 'st']

const metricColKeys = prefixes.map(prefix => 
  subjects.map(subject =>
    years.map(year =>
      units.map(unit =>
        suffixes.map(suffix =>
          prefix+'_'+subject+year+(unit ? '-'+unit : '')+(suffix ? '_'+suffix : '')
        ).flat()
      ).flat()
    ).flat()
  ).flat()
).flat()

const metricCols = metricColKeys.reduce((obj, key) => ({
  ...obj,
  [key]: true
}), {})

const AUTO_PARSE_COLS = {
  'id': false,
  'lon': true,
  'lat': true,
  // 'all_pef': true,
  'all_prm': true,
  'w_prm': true,
  'a_prm': true,
  'b_prm': true,
  'h_prm': true,
  'm_prm': true,
  'f_prm': true,
  'all_frl': true,
  'w_frl': true,
  'a_frl': true,
  'b_frl': true,
  'h_frl': true,
  'm_frl': true,
  'f_frl': true,
  'u': true,
  's': true,
  't': true,
  'r': true,
  'name': false,
  'e': true,
  'm': true,
  'c': true,
  'ch': true,
  'mg': true,
  'bie': true,
  'fid': true,
  ...metricCols,
}

const shapeData = (data, dataSetId) => {
  let filteredData = data
  // TEMPORARY: filter out BIE schools on non-embargoed link
  if (!SHOW_NATIVE_AMERICAN) {
    filteredData = filteredData.filter(d => !d.bie)
  }
  // TEMPORARY: filter out Puerto Rico data on non-embargoed link
  if (!SHOW_PUERTO_RICO) {
    filteredData = filteredData.filter(
      d => d.id.substr(0, 2) !== '72'
    )
  }
  // add a flag for 'regular' schools: non-magnet, non-charter, non-bie
  if (dataSetId === 'schools') {
    filteredData = filteredData.map(d => ({
      ...d,
      rg: !!d.bie || !!d.ch || !!d.mg ? null : 1
    }))
  }
  // sort data by number of students (`all_sz`)
  filteredData.sort((a, b) => b['all_sz'] - a['all_sz'])
  // HACK: rename `np_seg` to `pn_seg`, as it is a gap variable (not non-poor)
  // this can be removed when this issue is closed: https://github.com/Hyperobjekt/seda-etl/issues/35
  // mutates original to reduce performance impact
  HACK_FIX_NP_SEG(filteredData)
  return filteredData
}

/**
 *
 * @param {*} region
 */
const loadDataSet = (dataSetId, insertData, options = {}) => {
  const url = `${process.env.REACT_APP_DATA_ENDPOINT}/data/${dataSetId}.csv?v=${DATA_VERSION}`
  // console.log(url)
  // const result = []
  return new Promise((resolve, reject) => {
    readRemoteFile(url, {
      header: true,
      worker: true,
      dynamicTyping: AUTO_PARSE_COLS,
      // for streaming data:
      // step: row => {
      //   // insertData(dataSetId, [row.data])
      //   // const id = row.data.id
      //   result.push(row.data)
      // },
      complete: results => {
        resolve(results.data)
      },
      error: error => {
        console.error(error)
        resolve([])
        // reject(error)
      },
      ...options
    })
  })
}

/**
 * Renames `np_seg` data attribute to `pn_seg` due to pipeline error
 * @param {*} data
 */
const HACK_FIX_NP_SEG = data => {
  if (
    data &&
    data[0] &&
    Object.keys(data[0]).indexOf('np_seg') > -1
  ) {
    for (let i = 0; i < data.length; i++) {
      data[i]['pn_seg'] = data[i]['np_seg']
      delete data[i]['np_seg']
    }
  }
}

const useStaticData = create((set, get) => ({
  data: {},
  loading: [],
  loaded: [],
  timing: {},
  isLoading: false,
  insertData: (dataSetId, rows) => {
    set(state => {
      const currentData = state.data[dataSetId] || []
      const newData = [...currentData, ...rows].sort(
        (a, b) => b['all_sz'] - a['all_sz']
      )
      return { data: { ...state.data, [dataSetId]: newData } }
    })
  },
  loadDataSet: async dataSetId => {
    const t0 = performance.now()
    set(state => ({
      loading: [...state.loading, dataSetId],
      isLoading: true
    }))
    const data = await loadDataSet(dataSetId, get().insertData)

    const dataStore = shapeData(data, dataSetId)

    const loadTime = performance.now() - t0
    set(state => ({
      loading: state.loading.filter(v => v !== dataSetId),
      isLoading:
        state.loading.filter(v => v !== dataSetId).length > 0,
      loaded: [...state.loaded, dataSetId],
      data: { ...state.data, [dataSetId]: dataStore },
      timing: {
        ...state.timing,
        [dataSetId]: loadTime
      }
    }))
    logger.debug('loaded data:', dataSetId, dataStore, loadTime)

    const getPdfRegion = region => {
      return region === 'counties'
        ? 'county'
        : region === 'districts'
        ? 'district'
        : region === 'schools'
        ? 'school'
        : 'state'
    }
    return dataStore
  }
}))

export default useStaticData
